import { Box, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import GoogleDriveIcon from 'assets/icons/googledrive.svg';
import OneDriveIcon from 'assets/icons/onedrive.svg';
import FolderCellMenu from 'components/@home/@documents/FolderCellMenu';
import SyncBadge, { mirrorDisplayState } from 'components/@home/@documents/SyncBadge';
import AudioThumbnail from 'components/@home/@messages/ChatPanel/MessagesList/MessageGroup/AudioThumbnail';
import MessageIcon from 'components/common/MessageIcon';
import FolderArrowRightOutlineIcon from 'mdi-react/FolderArrowRightOutlineIcon';
import FolderOutlineIcon from 'mdi-react/FolderOutlineIcon';
import { object, oneOf, string } from 'prop-types';
import React from 'react';
import { Link } from 'react-router';
import {
  useDeleteDocumentFormatMutation,
  useDeleteSharedFileMutation,
  useEditDocumentFormatMutation,
  useEditSharedFileMutation,
  useRestoreDocumentFormatMutation,
  useRestoreSharedFileMutation,
  useSendReceivedMutation,
  useSendSharedFileMutation,
} from 'store/app/entities/SharedFilesSlice';
import documentModalActions from 'store/app/ui/modals/document/action';
import pictureModalActions from 'store/app/ui/modals/picture/action';
import videoModalActions from 'store/app/ui/modals/video/action';
import { getMessageType, MESSAGE_TYPES } from 'utils/getMessageType';
import useActions from 'utils/useActions';
import useOpenClose from 'utils/useOpenClose';

import ListItemButton from '@mui/material/ListItemButton';

const PROVIDER_ICONS = { google: GoogleDriveIcon, microsoft: OneDriveIcon };

const BaseItem = ({ folder, params, state = null, actions = {}, section, mirrors = {} }) => {
  const [isOpen, open, close] = useOpenClose();
  const [documentModal, pictureModal, videoModal] = useActions([
    documentModalActions,
    pictureModalActions,
    videoModalActions,
  ]);
  const isNavigable = folder !== 'deleted' && params.row.kind !== 'Document';
  const isDocument = params.row.kind === 'Document';
  const type = getMessageType({ attachments: [params.row] });
  const modalMap = {
    [MESSAGE_TYPES.VIDEO]: videoModal,
    [MESSAGE_TYPES.IMAGE]: pictureModal,
    [MESSAGE_TYPES.AUDIO]: { open: () => null },
  };
  const modal = modalMap[type] || documentModal;
  const isMirrorRoot =
    params.row.source?.readOnly && params.row.source?.externalId && !params.row.source?.mappingId;
  const mirror = isMirrorRoot ? mirrors[params.row.source?.externalId] || null : null;
  const ProviderIcon = PROVIDER_ICONS[mirror?.provider] || null;
  const rowIcon = (() => {
    switch (params.row.kind) {
      case 'Document':
        return <MessageIcon message={{ attachments: [params.row] }} />;
      case 'DocumentFormat':
        return folder === 'home' ? <FolderOutlineIcon /> : <FolderArrowRightOutlineIcon />;
      default:
        return isMirrorRoot && ProviderIcon ? (
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <FolderOutlineIcon />
            <Box
              component={ProviderIcon}
              sx={{
                position: 'absolute',
                right: '-4px',
                bottom: '-3px',
                width: 14,
                height: 14,
                color: 'background.paper',
              }}
            />
          </Box>
        ) : (
          <FolderOutlineIcon />
        );
    }
  })();
  return (
    <ListItem
      component="div"
      disablePadding
      secondaryAction={
        <FolderCellMenu
          folder={params.row}
          isOpen={isOpen}
          open={open}
          close={close}
          actions={actions}
          section={section}
          mirror={mirror}
        />
      }
      sx={{
        width: '100%',
        '@media screen and (min-width: 40em) and hover: hover': {
          '& .MuiListItemSecondaryAction-root': { display: isOpen ? 'block' : 'none' },
          '&:hover .MuiListItemSecondaryAction-root': { display: 'block' },
        },
      }}
    >
      <ListItemButton
        {...(isNavigable ? { component: Link } : {})}
        sx={{
          paddingLeft: 0,
          paddingRight: 6,
          '& .MuiListItemIcon-root img': {
            marginLeft: '2px',
            width: 20,
            height: 20,
          },
          // Size only the leading row icon — not nested icons like the sync badge
          // (in the name cell) or the kebab menu (in the secondary action).
          '& .MuiListItemIcon-root .MuiSvgIcon-root': {
            width: 24,
            height: 24,
          },
        }}
        {...(isNavigable
          ? {
              to: {
                pathname: `/home/documents/${folder}/${params.row._id}/`,
              },
              state: { ...state, parentName: params.row.name },
            }
          : {})}
        {...(isDocument
          ? {
              onClick: () =>
                modal.open(
                  params.row.httpLink,
                  null,
                  params.row.name,
                  false,
                  params.row.meta?.mimeType,
                ),
            }
          : {})}
      >
        <ListItemIcon>{rowIcon}</ListItemIcon>
        <ListItemText
          sx={{
            borderBottom: 0,
            minHeight: 0,
            paddingTop: 0,
            paddingBottom: 0,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
          }}
          slotProps={{
            primary: {
              noWrap: true,
              sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
            },
          }}
          primary={
            isMirrorRoot ? (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  maxWidth: '100%',
                  minWidth: 0,
                }}
              >
                <Box
                  component="span"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {params.value}
                </Box>
                {mirrorDisplayState(mirror) && <SyncBadge syncState={mirrorDisplayState(mirror)} />}
              </Box>
            ) : (
              params.value
            )
          }
        />
        {type === MESSAGE_TYPES.AUDIO && (
          <Box sx={{ flex: 0, minWidth: 300 }}>
            <AudioThumbnail data={params.row} />
          </Box>
        )}
      </ListItemButton>
    </ListItem>
  );
};

BaseItem.propTypes = {
  actions: object,
  folder: string.isRequired,
  mirrors: object,
  params: object.isRequired,
  section: oneOf(['chats', 'home', 'sent', 'shared']).isRequired,
  state: object,
};

const FolderItem = props => {
  const [destroy] = useDeleteSharedFileMutation();
  const [edit] = useEditSharedFileMutation();
  const [restore] = useRestoreSharedFileMutation();
  const [send] = useSendSharedFileMutation();
  const [sendMine] = useSendReceivedMutation();
  return <BaseItem {...props} actions={{ destroy, edit, restore, send, sendMine }} />;
};

const DocumentFormatItem = props => {
  const [destroy] = useDeleteDocumentFormatMutation();
  const [edit] = useEditDocumentFormatMutation();
  const [restore] = useRestoreDocumentFormatMutation();
  return <BaseItem {...props} actions={{ destroy, edit, restore }} />;
};

const renderFolderCell =
  (folder, state, section, mirrors = {}) =>
  params =>
    params.row.kind === 'DocumentFormat' ? (
      <DocumentFormatItem
        key={params.row._id}
        folder={folder}
        params={params}
        state={state}
        mirrors={mirrors}
      />
    ) : (
      <FolderItem
        key={params.row._id}
        folder={folder}
        params={params}
        state={state}
        section={section}
        mirrors={mirrors}
      />
    );

export default renderFolderCell;
